import {useEffect, useState} from 'react';
import { useQuery } from '@tanstack/react-query';
import { Outlet } from 'react-router';
import { styled } from '@mui/material/styles';
import { CommandPalette } from 'src/components/common';
import { AssignmentJobsIndicator } from 'src/components/common/AssignmentJobs';
import DashboardNavbar from './DashboardNavbar';
import DashboardSidebar from './DashboardSidebar';
import {getThemeOptions} from "../../../api/queries";
import useSettings from "../../../hooks/useSettings";

const DashboardLayoutRoot = styled('div')(({ theme }) => ({
  backgroundColor: theme.palette.background.default,
  display: 'flex',
  height: '100%',
  overflow: 'hidden',
  width: '100%'
}));

const DashboardLayoutWrapper = styled('div')(({ theme }) => ({
  display: 'flex',
  flex: '1 1 auto',
  overflow: 'hidden',
  paddingTop: '64px',
  [theme.breakpoints.up('lg')]: {
    paddingLeft: '280px'
  }
}));

const DashboardLayoutContainer = styled('div')({
  display: 'flex',
  flex: '1 1 auto',
  overflow: 'hidden'
});

const DashboardLayoutContent = styled('div')({
  flex: '1 1 auto',
  height: '100%',
  overflow: 'auto',
  position: 'relative',
  WebkitOverflowScrolling: 'touch'
});

const getValues = (settings) => ({
  compact: settings.compact,
  direction: settings.direction,
  responsiveFontSizes: settings.responsiveFontSizes,
  roundedCorners: settings.roundedCorners,
  theme: settings.theme,
  themeColor: settings.themeColor ?? "#2596be",
  companyName: settings.companyName ?? "TOKEN MDM",
  logo: settings.logo
});

const DashboardLayout = () => {
  const [isSidebarMobileOpen, setIsSidebarMobileOpen] = useState(false);
  const {settings, saveSettings} = useSettings();
  const [values, setValues] = useState(getValues(settings));

  useEffect(() => {
    saveSettings(values);
  }, [values])

  const themeOptionsQuery = useQuery({
    queryKey: [getThemeOptions.key],
    queryFn: () => getThemeOptions.func(null),
  });

  useEffect(() => {
    if (themeOptionsQuery.isSuccess) {
      const response = themeOptionsQuery.data;
      setValues(values => ({
        ...values,
        "themeColor": response.color,
        "companyName": response.name,
        "logo": response.icon
      }))
    }
  }, [themeOptionsQuery.isSuccess, themeOptionsQuery.data]);

  return (
    <DashboardLayoutRoot>
      <DashboardNavbar onSidebarMobileOpen={() => setIsSidebarMobileOpen(true)} />
      <DashboardSidebar
        onMobileClose={() => setIsSidebarMobileOpen(false)}
        openMobile={isSidebarMobileOpen}
      />
      <DashboardLayoutWrapper>
        <DashboardLayoutContainer>
          <DashboardLayoutContent>
            <AssignmentJobsIndicator />
            <Outlet />
          </DashboardLayoutContent>
        </DashboardLayoutContainer>
      </DashboardLayoutWrapper>
      <CommandPalette />
    </DashboardLayoutRoot>
  );
};

export default DashboardLayout;
